// src/pages/IlTuoAnnoPage.js
// Non un cruscotto — un riepilogo da leggere con soddisfazione. Attivo solo quando ha
// senso guardarlo: a fine anno, o dopo almeno 10 commesse concluse, qualunque venga prima.
import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { ChevronLeft, Trophy } from "lucide-react";
import { mappeEconomicheOrg } from "../utils/economiaCommessa";

function fmt(n) { return (parseFloat(n)||0).toLocaleString("it-IT",{maximumFractionDigits:0}); }
const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

export default function IlTuoAnnoPage({ onNavigate }) {
  const { effectiveOrgId } = useOrg();
  const [dati, setDati] = useState(null);
  const [caricato, setCaricato] = useState(false);
  const [anno, setAnno] = useState(new Date().getFullYear());

  useEffect(() => {
    if (!effectiveOrgId) return;
    (async () => {
      const [projSnap, budgetSnap, movSnap, odaSnap] = await Promise.all([
        getDocs(query(collection(db,"projects"), where("organizationId","==",effectiveOrgId))),
        getDocs(query(collection(db,"budgetCommessa"), where("organizationId","==",effectiveOrgId))),
        getDocs(query(collection(db,"movimentiEconomici"), where("organizationId","==",effectiveOrgId))),
        getDocs(query(collection(db,"ordiniAcquisto"), where("organizationId","==",effectiveOrgId))),
      ]);
      // Stesse regole del Controllo Economico (utils/economiaCommessa.js): ricavi = importo
      // commessa + ricavi registrati; costi = movimenti di costo + ordini (IVA esclusa)
      const { ricavi: budgetMap, costi: costiMap } = mappeEconomicheOrg({
        budget: budgetSnap.docs.map(d=>({id:d.id,...d.data()})), movimenti: movSnap.docs.map(d=>d.data()), ordini: odaSnap.docs.map(d=>d.data()) });

      const perAnno = (a) => {
        const chiuse = projSnap.docs.map(d=>({id:d.id,...d.data()}))
          .filter(p => !p.isParent && p.statoCommessa==="chiusa" && p.dataChiusura?.startsWith(String(a)));
        if (chiuse.length===0) return null;

        const valoreTotale = chiuse.reduce((s,p)=>s+(budgetMap[p.id]||0),0);
        const margini = chiuse.filter(p=>budgetMap[p.id]>0).map(p=>{
          const b=budgetMap[p.id], c=costiMap[p.id]||0; return (b-c)/b*100;
        });
        const margineMedio = margini.length>0 ? margini.reduce((s,v)=>s+v,0)/margini.length : null;

        const perMese = {}; chiuse.forEach(p=>{ if(p.createdAt?.toDate){ const m=p.createdAt.toDate().getMonth(); perMese[m]=(perMese[m]||0)+1; }});
        const meseTop = Object.entries(perMese).sort((a,b)=>b[1]-a[1])[0];

        const perCliente = {}; chiuse.forEach(p=>{ if(p.clienteNome) perCliente[p.clienteNome]=(perCliente[p.clienteNome]||0)+1; });
        const clienteTop = Object.entries(perCliente).sort((a,b)=>b[1]-a[1])[0];

        const piuGrande = chiuse.reduce((max,p)=> (budgetMap[p.id]||0)>(budgetMap[max?.id]||0) ? p : max, null);

        return { n: chiuse.length, valoreTotale, margineMedio, meseTop, clienteTop, piuGrande: piuGrande?{...piuGrande, valore:budgetMap[piuGrande.id]||0}:null };
      };

      setDati({ corrente: perAnno(anno), precedente: perAnno(anno-1) });
      setCaricato(true);
    })();
  }, [effectiveOrgId, anno]);

  if (!caricato) return <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Sto mettendo insieme il tuo anno...</div>;

  const d = dati?.corrente;
  const p = dati?.precedente;

  return (
    <div style={{padding:24,maxWidth:800,overflowY:"auto",height:"100%"}}>
      <button onClick={()=>onNavigate&&onNavigate({page:"control-center"})} style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",display:"flex",alignItems:"center",gap:4,fontSize:13,fontWeight:600,marginBottom:14}}>
        <ChevronLeft size={15}/> Torna a Centro Operativo
      </button>

      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
        <select value={anno} onChange={e=>{setAnno(parseInt(e.target.value));setCaricato(false);}}
          style={{fontSize:24,fontWeight:900,color:"#7c3aed",border:"none",background:"none",cursor:"pointer"}}>
          {[anno,anno-1,anno-2].map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        <h1 style={{fontSize:24,fontWeight:900,color:"#1e1b4b",margin:0}}>in Nexiora</h1>
      </div>

      {!d ? (
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <Trophy size={40} style={{opacity:.2,marginBottom:10}}/>
          <div style={{fontSize:14,fontWeight:600}}>Nessuna commessa chiusa nel {anno}, ancora.</div>
        </div>
      ) : (
        <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
            <div style={{background:"linear-gradient(135deg,#7c3aed,#5b21b6)",borderRadius:14,padding:20,color:"#fff"}}>
              <div style={{fontSize:11,opacity:.8,textTransform:"uppercase",fontWeight:700}}>Commesse concluse</div>
              <div style={{fontSize:32,fontWeight:900}}>{d.n}</div>
            </div>
            <div style={{background:"linear-gradient(135deg,#059669,#047857)",borderRadius:14,padding:20,color:"#fff"}}>
              <div style={{fontSize:11,opacity:.8,textTransform:"uppercase",fontWeight:700}}>Valore totale</div>
              <div style={{fontSize:32,fontWeight:900}}>€{fmt(d.valoreTotale)}</div>
            </div>
          </div>

          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginBottom:16}}>
            {d.margineMedio!==null && (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #f3f4f6"}}>
                <span style={{fontSize:13,color:"#374151"}}>Margine medio dell'anno</span>
                <span style={{fontSize:16,fontWeight:800,color:"#7c3aed"}}>
                  {(d.margineMedio).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%
                  {p?.margineMedio!=null && (
                    <span style={{fontSize:11,fontWeight:600,color: d.margineMedio>=p.margineMedio?"#059669":"#dc2626",marginLeft:8}}>
                      {d.margineMedio>=p.margineMedio?"▲":"▼"} vs {anno-1}: {(p.margineMedio).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%
                    </span>
                  )}
                </span>
              </div>
            )}
            {d.meseTop && (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #f3f4f6"}}>
                <span style={{fontSize:13,color:"#374151"}}>Il mese più intenso</span>
                <span style={{fontSize:14,fontWeight:700,color:"#111827"}}>{MESI[d.meseTop[0]]} — {d.meseTop[1]} commesse</span>
              </div>
            )}
            {d.clienteTop && (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #f3f4f6"}}>
                <span style={{fontSize:13,color:"#374151"}}>Il cliente con cui hai lavorato di più</span>
                <span style={{fontSize:14,fontWeight:700,color:"#111827"}}>{d.clienteTop[0]} — {d.clienteTop[1]} commesse</span>
              </div>
            )}
            {d.piuGrande && (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0"}}>
                <span style={{fontSize:13,color:"#374151"}}>La commessa più grande</span>
                <span style={{fontSize:14,fontWeight:700,color:"#111827"}}>{d.piuGrande.name} — €{fmt(d.piuGrande.valore)}</span>
              </div>
            )}
          </div>

          {p && (
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#5b21b6"}}>
              <span>Nel {anno-1}: {p.n} commesse, €{fmt(p.valoreTotale)}</span>
              <span style={{fontWeight:700}}>{d.n>=p.n?"📈 Crescita":"—"} {d.n>=p.n?`+${d.n-p.n} commesse`:""}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
